import { formatDate } from '@angular/common';
import { Injectable, Injector, ViewChild } from '@angular/core';
import { Validators, FormGroup, FormArray } from '@angular/forms';
import { Router } from '@angular/router';
import { STColumn, STComponent } from '@delon/abc/st';
import { _HttpClient } from '@delon/theme';
import { SstReturnDeemedPaymentModalComponent } from '@shared-feature/components/modal/sst-return-modal/deemed-payment-modal/sst-return-deemed-payment-modal.component';
import { SstReturnDeemedPaymentRecoveryModalComponent } from '@shared-feature/components/modal/sst-return-modal/deemed-payment-recovery-modal/sst-return-deemed-payment-recovery-modal.component';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService, NzModalRef } from 'ng-zorro-antd/modal';
import { Observable, of, concat, throwError, forkJoin, Subject } from 'rxjs';
import { map, switchMap, last, tap, catchError } from 'rxjs/operators';
import { DocNavService } from 'src/app/core/util/doc-nav.service';
import { DataBaseService } from 'src/app/shared-feature/services/_base/data-base.service';

import { ExclusionModalComponent } from './exclusion-modal.component';

/**
 * Service for managing SST Return operations
 *
 * Handles creation, update, and retrieval of SST returns and their associated journal entries.
 */

// Add at the top of your detail.component.ts or in a separate models file
export interface DetailListItem {
  docDate: string;
  taxDate: string;
  docType: string;
  docCode: string;
  stockCode: string;
  itemDescription: string;
  taxExclusiveAmountLocal: number;
  taxAmountLocal: number;
  netAmountLocal: number;
}

export interface DetailItem {
  taxCode: string;
  totalTaxableAmountLocal: number;
  totalTaxAmountLocal: number;
  totalNetAmountLocal: number;
  detailLists: DetailListItem[];
}

// New interfaces for grouped deemed payment data
export interface DeemedPaymentGroupItem {
  taxCode: string;
  totalTaxableAmountLocal: number;
  totalTaxAmountLocal: number;
  totalNetAmountLocal: number;
  sstDeemedPaymentDetailList: any[];
}

export interface DeemedPaymentRecoveryGroupItem {
  taxCode: string;
  totalOsTaxableAmount: number;
  totalOsTaxAmountLocal: number;
  totalOsNetAmountLocal: number;
  totalTaxableAmountLocal: number;
  totalTaxAmountLocal: number;
  sstDeemedPaymentRecoveryDetailList: any[];
}

@Injectable()
export class SstReturnService extends DataBaseService {
  // Form and data properties
  form = this.createForm();
  deemedPaymentItems: any[] = [];
  deemedPaymentRecoveryItems: any[] = [];
  // Excluded items
  excludedDeemedPaymentItems: any[] = [];
  excludedDeemedPaymentRecoveryItems: any[] = [];

  // Grouped data for UI display (similar to Part B1)
  deemedPaymentGroups: DeemedPaymentGroupItem[] = [];
  deemedPaymentRecoveryGroups: DeemedPaymentRecoveryGroupItem[] = [];
  excludedDeemedPaymentGroups: DeemedPaymentGroupItem[] = [];
  excludedDeemedPaymentRecoveryGroups: DeemedPaymentRecoveryGroupItem[] = [];

  // Original data backup for cancel operation
  private _originalDeemedPaymentItems: any[] = [];
  private _originalExcludedDeemedPaymentItems: any[] = [];
  private _originalDeemedPaymentRecoveryItems: any[] = [];
  private _originalExcludedDeemedPaymentRecoveryItems: any[] = [];

  // Selection properties
  selectedDeemedPaymentRows: any[] = [];
  selectedDeemedPaymentRecoveryRows: any[] = [];
  selectedExcludedDeemedPaymentRows: any[] = [];
  selectedExcludedDeemedPaymentRecoveryRows: any[] = [];

  // Modal references
  private modalRef: SstReturnDeemedPaymentModalComponent | null = null;
  private dprModalRef: SstReturnDeemedPaymentRecoveryModalComponent | null =
    null;

  private message: NzMessageService;

  constructor(injector: Injector) {
    super(injector);
    this.aclKey = 'sstreturn';
    this.message = injector.get(NzMessageService);
  }

  //#region Public API

  /**
   * Sets the reference to the deemed payment modal
   */
  setModalRef(modal: SstReturnDeemedPaymentModalComponent) {
    this.modalRef = modal;
  }

  /**
   * Sets the reference to the deemed payment recovery modal
   */
  setDPRModalRef(modal: SstReturnDeemedPaymentRecoveryModalComponent) {
    this.dprModalRef = modal;
  }

  private filterDPGroupsByExclusion(
    groups: DeemedPaymentGroupItem[],
    isExcluded: boolean
  ) {
    return groups
      .map(group => ({
        ...group,
        sstDeemedPaymentDetailList: group.sstDeemedPaymentDetailList.filter(
          detail => detail.isExcluded === isExcluded
        )
      }))
      .filter(group => group.sstDeemedPaymentDetailList.length > 0);
  }

  private filterDPRGroupsByExclusion(
    groups: DeemedPaymentRecoveryGroupItem[],
    isExcluded: boolean
  ) {
    return groups
      .map(group => ({
        ...group,
        sstDeemedPaymentRecoveryDetailList:
          group.sstDeemedPaymentRecoveryDetailList.filter(
            detail => detail.isExcluded === isExcluded
          )
      }))
      .filter(group => group.sstDeemedPaymentRecoveryDetailList.length > 0);
  }

  /**
   * Groups deemed payment items by tax code (similar to Part B1 grouping)
   */
  private groupDeemedPaymentItems(items: any[]): DeemedPaymentGroupItem[] {
    const grouped = new Map<string, DeemedPaymentGroupItem>();

    items.forEach(item => {
      const taxCode = item.taxCode.code || 'Unknown';

      if (!grouped.has(taxCode)) {
        grouped.set(taxCode, {
          taxCode,
          totalTaxableAmountLocal: 0,
          totalTaxAmountLocal: 0,
          totalNetAmountLocal: 0,
          sstDeemedPaymentDetailList: []
        });
      }

      const group = grouped.get(taxCode)!;
      group.sstDeemedPaymentDetailList.push(item);

      group.totalTaxableAmountLocal += item.osTaxableAmount || 0;
      group.totalTaxAmountLocal += item.osTaxAmountLocal || 0;
      group.totalNetAmountLocal += item.osNetAmountLocal || 0;
    });

    return Array.from(grouped.values());
  }

  /**
   * Groups deemed payment recovery items by tax code (similar to Part B1 grouping)
   */
  private groupDeemedPaymentRecoveryItems(
    items: any[]
  ): DeemedPaymentRecoveryGroupItem[] {
    const grouped = new Map<string, DeemedPaymentRecoveryGroupItem>();

    items.forEach(item => {
      const taxCode = item.taxCode.code || 'Unknown';

      if (!grouped.has(taxCode)) {
        grouped.set(taxCode, {
          taxCode,
          totalOsTaxableAmount: 0,
          totalOsTaxAmountLocal: 0,
          totalOsNetAmountLocal: 0,
          totalTaxableAmountLocal: 0,
          totalTaxAmountLocal: 0,
          sstDeemedPaymentRecoveryDetailList: []
        });
      }

      const group = grouped.get(taxCode)!;
      group.sstDeemedPaymentRecoveryDetailList.push(item);

      group.totalOsTaxableAmount += item.osTaxableAmount || 0;
      group.totalOsTaxAmountLocal += item.osTaxAmountLocal || 0;
      group.totalOsNetAmountLocal += item.osNetAmountLocal || 0;
      group.totalTaxableAmountLocal += item.taxableAmountLocal || 0;
      group.totalTaxAmountLocal += item.taxAmountLocal || 0;
    });

    return Array.from(grouped.values());
  }

  /**
   * Gets the document navigation service
   */
  get docNav() {
    return this.injector.get(DocNavService);
  }

  /**
   * Creates a clone of the current data, removing the ID
   */
  clone() {
    let obj = { ...this._data };
    delete obj.id;
    return obj;
  }

  /**
   * Creates a new SST Return object
   */
  new(taxableDateFrom?: string, taxableDateTo?: string) {
    // Get the active tab from localStorage
    const activeTabIndex = localStorage.getItem('sstreturn_active_tab');
    // Determine type based on active tab (0 = SALES, 1 = SERVICE)
    const type = activeTabIndex === '1' ? 'SERVICE' : 'SALES';

    // Build query parameters, only including dates if they exist
    let queryParams = `type=${type}`;
    if (taxableDateFrom) {
      queryParams += `&dateFrom=${taxableDateFrom}`;
    }
    if (taxableDateTo) {
      queryParams += `&dateTo=${taxableDateTo}`;
    }

    // Pass the type as a query parameter to the API
    return this.http.get(`api/sstreturns/new?${queryParams}`).pipe(
      tap((res: any) => {
        // Process deemed payment items
        if (res.sstDeemedPayments && Array.isArray(res.sstDeemedPayments)) {
          // Ensure each item has the isExcluded property defined as a boolean
          const processedItems = res.sstDeemedPayments.map((item: any) => ({
            ...item,
            isExcluded: item.isExcluded === true // Strict boolean comparison
          }));

          // Split items into included and excluded arrays
          this.deemedPaymentItems = processedItems.filter(
            (item: any) => !item.isExcluded
          );
          this.excludedDeemedPaymentItems = processedItems.filter(
            (item: any) => item.isExcluded
          );

          // Create grouped data for UI display (use API data if available, otherwise group locally)
          if (
            res.sstDeemedPaymentList &&
            Array.isArray(res.sstDeemedPaymentList)
          ) {
            this.deemedPaymentGroups = this.filterDPGroupsByExclusion(
              res.sstDeemedPaymentList,
              false
            );
            this.excludedDeemedPaymentGroups = this.filterDPGroupsByExclusion(
              res.sstDeemedPaymentList,
              true
            );
            //  .map((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentDetailList: group.sstDeemedPaymentDetailList.filter((detail: any) => !detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentDetailList.length > 0);

            // this.excludedDeemedPaymentGroups = res.sstDeemedPaymentList.filter((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentDetailList: group.sstDeemedPaymentDetailList.filter((detail: any) => detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentDetailList.length > 0);
          } else {
            this.deemedPaymentGroups = this.groupDeemedPaymentItems(
              this.deemedPaymentItems
            );
            this.excludedDeemedPaymentGroups = this.groupDeemedPaymentItems(
              this.excludedDeemedPaymentItems
            );
          }

          // Store deep copies of original data for cancel operation
          this._originalDeemedPaymentItems = JSON.parse(
            JSON.stringify(this.deemedPaymentItems)
          );
          this._originalExcludedDeemedPaymentItems = JSON.parse(
            JSON.stringify(this.excludedDeemedPaymentItems)
          );
        } else {
          this.deemedPaymentItems = [];
          this.excludedDeemedPaymentItems = [];
          this.deemedPaymentGroups = [];
          this.excludedDeemedPaymentGroups = [];
          this._originalDeemedPaymentItems = [];
          this._originalExcludedDeemedPaymentItems = [];
        }

        // Process deemed payment recovery items
        if (
          res.sstDeemedPaymentRecovery &&
          Array.isArray(res.sstDeemedPaymentRecovery)
        ) {
          // Ensure each item has the isExcluded property defined as a boolean
          const processedItems = res.sstDeemedPaymentRecovery.map(
            (item: any) => ({
              ...item,
              isExcluded: item.isExcluded === true // Strict boolean comparison
            })
          );

          // Split items into included and excluded arrays
          this.deemedPaymentRecoveryItems = processedItems.filter(
            (item: any) => !item.isExcluded
          );
          this.excludedDeemedPaymentRecoveryItems = processedItems.filter(
            (item: any) => item.isExcluded
          );

          // Create grouped data for UI display (use API data if available, otherwise group locally)
          if (
            res.sstDeemedPaymentRecoveryList &&
            Array.isArray(res.sstDeemedPaymentRecoveryList)
          ) {
            this.deemedPaymentRecoveryGroups = this.filterDPRGroupsByExclusion(
              res.sstDeemedPaymentRecoveryList,
              false
            );
            this.excludedDeemedPaymentRecoveryGroups =
              this.filterDPRGroupsByExclusion(
                res.sstDeemedPaymentRecoveryList,
                true
              );
            // this.deemedPaymentRecoveryGroups = res.sstDeemedPaymentRecoveryList.filter((group: any) =>
            //   group.sstDeemedPaymentRecoveryDetailList && !group.sstDeemedPaymentRecoveryDetailList.isExcluded
            // );
            // this.excludedDeemedPaymentRecoveryGroups = res.sstDeemedPaymentRecoveryList.filter((group: any) =>
            //   group.sstDeemedPaymentRecoveryDetailList && group.sstDeemedPaymentRecoveryDetailList.isExcluded
            // );
          } else {
            this.deemedPaymentRecoveryGroups =
              this.groupDeemedPaymentRecoveryItems(
                this.deemedPaymentRecoveryItems
              );
            this.excludedDeemedPaymentRecoveryGroups =
              this.groupDeemedPaymentRecoveryItems(
                this.excludedDeemedPaymentRecoveryItems
              );
          }

          // Store deep copies of original data for cancel operation
          this._originalDeemedPaymentRecoveryItems = JSON.parse(
            JSON.stringify(this.deemedPaymentRecoveryItems)
          );
          this._originalExcludedDeemedPaymentRecoveryItems = JSON.parse(
            JSON.stringify(this.excludedDeemedPaymentRecoveryItems)
          );
        } else {
          this.deemedPaymentRecoveryItems = [];
          this.excludedDeemedPaymentRecoveryItems = [];
          this.deemedPaymentRecoveryGroups = [];
          this.excludedDeemedPaymentRecoveryGroups = [];
          this._originalDeemedPaymentRecoveryItems = [];
          this._originalExcludedDeemedPaymentRecoveryItems = [];
        }

        // Process details FormArray
        const detailsArray = this.form.get('details') as FormArray;
        detailsArray.clear();
        (res.details || []).forEach((detail: any) => {
          detailsArray.push(this.buildDetailGroup(detail));
        });

        this.form.controls.totalPayableTax.setValue(res.totalPayableTax || 0);

        // Force refresh of data tables after loading
        this.refreshAllTables();
      })
    );
  }

  /**
   * Retrieves an SST Return by ID
   */
  get(id: string): Observable<any> {
    return this.http.get(`api/sstreturns/${id}`).pipe(
      tap((res: any) => {
        // Process deemed payment items
        if (res.sstDeemedPayments && Array.isArray(res.sstDeemedPayments)) {
          // Ensure each item has the isExcluded property defined as a boolean
          const processedItems = res.sstDeemedPayments.map((item: any) => ({
            ...item,
            isExcluded: item.isExcluded === true // Strict boolean comparison
          }));

          // Split items into included and excluded arrays
          this.deemedPaymentItems = processedItems.filter(
            (item: any) => !item.isExcluded
          );
          this.excludedDeemedPaymentItems = processedItems.filter(
            (item: any) => item.isExcluded
          );

          // Create grouped data for UI display (use API data if available, otherwise group locally)
          if (
            res.sstDeemedPaymentList &&
            Array.isArray(res.sstDeemedPaymentList)
          ) {
            this.deemedPaymentGroups = this.filterDPGroupsByExclusion(
              res.sstDeemedPaymentList,
              false
            );
            this.excludedDeemedPaymentGroups = this.filterDPGroupsByExclusion(
              res.sstDeemedPaymentList,
              true
            );

            // .map((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentDetailList: group.sstDeemedPaymentDetailList.filter((detail: any) => !detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentDetailList.length > 0);

            // this.excludedDeemedPaymentGroups = res.sstDeemedPaymentList.map((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentDetailList: group.sstDeemedPaymentDetailList.filter((detail: any) => detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentDetailList.length > 0);
          } else {
            this.deemedPaymentGroups = this.groupDeemedPaymentItems(
              this.deemedPaymentItems
            );
            this.excludedDeemedPaymentGroups = this.groupDeemedPaymentItems(
              this.excludedDeemedPaymentItems
            );
          }

          // Store deep copies of original data for cancel operation
          this._originalDeemedPaymentItems = JSON.parse(
            JSON.stringify(this.deemedPaymentItems)
          );
          this._originalExcludedDeemedPaymentItems = JSON.parse(
            JSON.stringify(this.excludedDeemedPaymentItems)
          );
        } else {
          this.deemedPaymentItems = [];
          this.excludedDeemedPaymentItems = [];
          this.deemedPaymentGroups = [];
          this.excludedDeemedPaymentGroups = [];
          this._originalDeemedPaymentItems = [];
          this._originalExcludedDeemedPaymentItems = [];
        }

        // Process deemed payment recovery items
        if (
          res.sstDeemedPaymentRecovery &&
          Array.isArray(res.sstDeemedPaymentRecovery)
        ) {
          // Ensure each item has the isExcluded property defined as a boolean
          const processedItems = res.sstDeemedPaymentRecovery.map(
            (item: any) => ({
              ...item,
              isExcluded: item.isExcluded === true // Strict boolean comparison
            })
          );

          // Split items into included and excluded arrays
          this.deemedPaymentRecoveryItems = processedItems.filter(
            (item: any) => !item.isExcluded
          );
          this.excludedDeemedPaymentRecoveryItems = processedItems.filter(
            (item: any) => item.isExcluded
          );

          // Create grouped data for UI display (use API data if available, otherwise group locally)
          if (
            res.sstDeemedPaymentRecoveryList &&
            Array.isArray(res.sstDeemedPaymentRecoveryList)
          ) {
            this.deemedPaymentRecoveryGroups = this.filterDPRGroupsByExclusion(
              res.sstDeemedPaymentRecoveryList,
              false
            );
            this.excludedDeemedPaymentRecoveryGroups =
              this.filterDPRGroupsByExclusion(
                res.sstDeemedPaymentRecoveryList,
                true
              );
            // this.deemedPaymentRecoveryGroups = res.sstDeemedPaymentRecoveryList.map((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentRecoveryDetailList: group.sstDeemedPaymentRecoveryDetailList.filter((detail: any) => !detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentRecoveryDetailList.length > 0);

            // this.excludedDeemedPaymentRecoveryGroups = res.sstDeemedPaymentRecoveryList.map((group: any) => ({
            //   ...group,
            //   sstDeemedPaymentRecoveryDetailList: group.sstDeemedPaymentRecoveryDetailList.filter((detail: any) => detail.isExcluded)
            // })).filter((group: any) => group.sstDeemedPaymentRecoveryDetailList.length > 0);
          } else {
            this.deemedPaymentRecoveryGroups =
              this.groupDeemedPaymentRecoveryItems(
                this.deemedPaymentRecoveryItems
              );
            this.excludedDeemedPaymentRecoveryGroups =
              this.groupDeemedPaymentRecoveryItems(
                this.excludedDeemedPaymentRecoveryItems
              );
          }

          // Store deep copies of original data for cancel operation
          this._originalDeemedPaymentRecoveryItems = JSON.parse(
            JSON.stringify(this.deemedPaymentRecoveryItems)
          );
          this._originalExcludedDeemedPaymentRecoveryItems = JSON.parse(
            JSON.stringify(this.excludedDeemedPaymentRecoveryItems)
          );
        } else {
          this.deemedPaymentRecoveryItems = [];
          this.excludedDeemedPaymentRecoveryItems = [];
          this.deemedPaymentRecoveryGroups = [];
          this.excludedDeemedPaymentRecoveryGroups = [];
          this._originalDeemedPaymentRecoveryItems = [];
          this._originalExcludedDeemedPaymentRecoveryItems = [];
        }

        // Process details FormArray
        const detailsArray = this.form.get('details') as FormArray;
        detailsArray.clear();
        (res.details || []).forEach((detail: any) => {
          detailsArray.push(this.buildDetailGroup(detail));
        });

        // Force refresh of data tables after loading
        this.refreshAllTables();
      })
    );
  }

  /**
   * Regenerate and attach SST Return reports (SST Return, Part B1, Detail Listing PDFs).
   */
  generateAttachments(id: number): Observable<any> {
    return this.http.post(`api/sstreturns/GenerateAndAttachReports/${id}`);
  }

  get details(): DetailItem[] {
    return this.form.controls.details.value as DetailItem[];
  }

  /**
   * Override validation method
   */
  override validate(): boolean {
    this.observeChanges = false;
    return super.validate();
  }

  /**
   * Creates a new SST Return with optional JV generation
   *
   * Flow:
   * 1. Show confirmation modals for deemed payments/recovery if applicable
   * 2. Create SST Return
   * 3. Generate journals using the created SST Return
   * 4. Refresh data to update journal codes
   *
   * @param body The data to create the SST return
   * @param options Additional options for creation
   * @returns An Observable with the created SST return
   */
  create(body: any) {
    const model = { ...body };
    const hasDP = this.deemedPaymentItems?.length > 0;
    const hasDPR = this.deemedPaymentRecoveryItems?.length > 0;
    const hasExcludedDP = this.excludedDeemedPaymentItems?.length > 0;
    const hasExcludedDPR = this.excludedDeemedPaymentRecoveryItems?.length > 0;
    const modalService = this.injector.get(NzModalService);
    const router = this.injector.get(Router);

    // Include both active and excluded deemed payment items
    if (hasDP || hasExcludedDP) {
      // Important: Ensure isExcluded is explicitly set for all items
      model.sstDeemedPayments = [
        ...(this.deemedPaymentItems || []).map(item => ({
          ...item,
          isExcluded: false, // Explicitly mark as not excluded
          excludeReason: null
        })),
        ...(this.excludedDeemedPaymentItems || []).map(item => ({
          ...item,
          isExcluded: true, // Explicitly mark as excluded
          excludeReason: item.excludeReason || 'Manually excluded'
        }))
      ];
    }

    // Include both active and excluded deemed payment recovery items
    if (hasDPR || hasExcludedDPR) {
      // Important: Ensure isExcluded is explicitly set for all items
      model.sstDeemedPaymentRecovery = [
        ...(this.deemedPaymentRecoveryItems || []).map(item => ({
          ...item,
          isExcluded: false,
          excludeReason: ''
        })),
        ...(this.excludedDeemedPaymentRecoveryItems || []).map(item => ({
          ...item,
          isExcluded: true,
          excludeReason: item.excludeReason || 'Manually excluded'
        }))
      ];
    }

    // Function to create SST Return

    return this.http.post('api/sstreturns/create/', model);

    // Show modal to confirm deemed payment details
    // const confirmModal = (modalRef: any, assignFn: () => void): Observable<void> =>
    //   new Observable<void>(sub => {
    //     modalRef.show();
    //     const subConfirm = modalRef.confirm.subscribe((confirmed: boolean) => {
    //       subConfirm.unsubscribe();
    //       if (confirmed) assignFn();
    //       sub.next();
    //       sub.complete();
    //     });
    //   });

    // // Generate journal entries after creating the SST Return
    // const generateJournals = (sstReturn: any): Observable<any> => {
    //   // If no deemed payments or not a service tax return, return immediately
    //   if (model.type !== 1 ||
    //       (!hasDP || !model.sstDeemedPayments?.length) &&
    //       (!hasDPR || !model.sstDeemedPaymentRecovery?.length)) {
    //     return of(sstReturn);
    //   }

    //   // Create an array to store the API calls
    //   const calls: Array<Observable<any>> = [];

    //   // Add deemed payment journal generation if needed
    //   if (hasDP && model.sstDeemedPayments?.length > 0) {
    //     calls.push(this.http.post('api/journals/GenerateDeemedPaymentJV/', sstReturn));
    //   }

    //   // Add deemed payment recovery journal generation if needed
    //   if (hasDPR && model.sstDeemedPaymentRecovery?.length > 0) {
    //     calls.push(this.http.post('api/journals/GenerateDeemedPaymentRecoveryJV/', sstReturn));
    //   }

    //   // Execute all calls in parallel, but stop on first error
    //   return forkJoin(calls).pipe(
    //     map(() => sstReturn), // Return the sstReturn after generating journals
    //     catchError(err => {
    //       if (this.isSstSettingsMissingError(err)) {
    //         return new Observable(observer => {
    //           // Reset loading state immediately
    //           this.loading = false;

    //           // Show confirmation modal
    //           modalService.confirm({
    //             nzTitle: 'SST Settings Missing',
    //             nzContent: 'SST settings are missing or incomplete. Do you want to go to the SST settings page?',
    //             nzOnOk: () => {
    //               // Navigate to SST settings
    //               router.navigate(['./tax/sstsettings']);
    //               // Propagate the error
    //               observer.error(err);
    //             },
    //             nzOnCancel: () => {
    //               // Propagate the error for error handling
    //               observer.error(err);
    //             }
    //           });
    //         });
    //       }
    //       return throwError(() => err);
    //     })
    //   );
    // };

    // // Function to explicitly refresh journal data after creation
    // const refreshJournalData = (sstReturn: any): Observable<any> => {
    //   if (sstReturn && sstReturn.id) {
    //     // Create refresh calls based on which data we need to update
    //     const refreshCalls: Array<Observable<any>> = [];

    //     // If we created deemed payment journals, refresh the deemed payment data
    //     if (hasDP && model.sstDeemedPayments?.length > 0) {
    //       const startDate = formatDate(model.taxableDateFrom, 'yyyy-MM-dd', 'en-US');
    //       const endDate = formatDate(model.taxableDateTo, 'yyyy-MM-dd', 'en-US');
    //       refreshCalls.push(
    //         this.http.get(`api/sstreturns/GetDeemedPaymentData?startDate=${startDate}&endDate=${endDate}&sstReturnId=${sstReturn.id}`).pipe(
    //           tap((dpData: any[]) => {
    //             this.deemedPaymentItems = dpData || [];
    //           })
    //         )
    //       );
    //     }

    //     // If we created deemed payment recovery journals, refresh that data
    //     if (hasDPR && model.sstDeemedPaymentRecovery?.length > 0) {
    //       const startDate = formatDate(model.taxableDateFrom, 'yyyy-MM-dd', 'en-US');
    //       const endDate = formatDate(model.taxableDateTo, 'yyyy-MM-dd', 'en-US');
    //       refreshCalls.push(
    //         this.http.get(`api/sstreturns/GetDeemedPaymentRecoveryData?startDate=${startDate}&endDate=${endDate}&sstReturnId=${sstReturn.id}`).pipe(
    //           tap((dprData: any[]) => {
    //             this.deemedPaymentRecoveryItems = dprData || [];
    //           })
    //         )
    //       );
    //     }

    //     // If we have refresh calls, execute them and then return the original response
    //     if (refreshCalls.length > 0) {
    //       return forkJoin(refreshCalls).pipe(
    //         map(() => sstReturn), // Return the original response after refreshing data
    //         catchError(err => {
    //           return of(sstReturn); // Return original response even if refresh fails
    //         })
    //       );
    //     }
    //   }

    //   // If no refresh calls or no ID, just return the original response
    //   return of(sstReturn);
    // };

    // return new Observable(subscriber => {
    // const tasks: Array<Observable<void>> = [];

    // // Build confirmation tasks if needed
    // if (model.type === 1) {
    //   if (hasDP && this.modalRef) {
    //     tasks.push(confirmModal(this.modalRef, () => {
    //       // Keep both active and excluded items
    //       model.sstDeemedPayments = [
    //         ...(this.deemedPaymentItems || []).map(item => ({
    //           ...item,
    //           isExcluded: false,
    //           excludeReason: ''
    //         })),
    //         ...(this.excludedDeemedPaymentItems || []).map(item => ({
    //           ...item,
    //           isExcluded: true,
    //           excludeReason: item.excludeReason || 'Manually excluded'
    //         }))
    //       ];
    //     }));
    //   }

    //   if (hasDPR && this.dprModalRef) {
    //     tasks.push(confirmModal(this.dprModalRef, () => {
    //       // Keep both active and excluded items
    //       model.sstDeemedPaymentRecovery = [
    //         ...(this.deemedPaymentRecoveryItems || []).map(item => ({
    //           ...item,
    //           isExcluded: false,
    //           excludeReason: ''
    //         })),
    //         ...(this.excludedDeemedPaymentRecoveryItems || []).map(item => ({
    //           ...item,
    //           isExcluded: true,
    //           excludeReason: item.excludeReason || 'Manually excluded'
    //         }))
    //       ];
    //     }));
    //   }
    // }

    // // Show confirmation modal for journal generation
    // modalService.confirm({
    //   nzTitle: 'Generate Journals',
    //   nzContent: 'Do you want to generate journals for this SST return?',
    //   nzOnOk: () => {
    //     // Execute tasks, then create SST Return, then generate journals, then refresh journal data
    //     (tasks.length
    //       ? concat(...tasks).pipe(
    //           last(),
    //           switchMap(() => createSstReturn()),
    //           switchMap(res => generateJournals(res)),
    //           switchMap(res => refreshJournalData(res))
    //         )
    //       : createSstReturn().pipe(
    //           switchMap(res => generateJournals(res)),
    //           switchMap(res => refreshJournalData(res))
    //         )
    //     ).subscribe({
    //       next: (res: any) => {
    //         // Reset loading state on success
    //         this.loading = false;

    //         // Force UI update if needed
    //         this.refreshSTComponents();

    //         // Trigger event for attachment handling
    //         this.eventTrigger.next({ id: res.id });

    //         subscriber.next(res);
    //         subscriber.complete();
    //       },
    //       error: (err: any) => {
    //         // Reset loading state on error
    //         this.loading = false;

    //         // Check for SST settings errors
    //         if (this.isSstSettingsMissingError(err)) {
    //           modalService.confirm({
    //             nzTitle: 'SST Settings Missing',
    //             nzContent: 'SST settings are missing or incomplete. Do you want to go to the SST settings page?',
    //             nzOnOk: () => {
    //               router.navigate(['./tax/sstsettings']);
    //             }
    //           });
    //         }

    //         subscriber.error(err);
    //       }
    //     });
    //   },
    //   nzOnCancel: () => {
    //     // Just create SST Return without generating journals
    //     createSstReturn().subscribe({
    //       next: (res: any) => {
    //         // Reset loading state on success
    //         this.loading = false;

    //         // Force UI update if needed
    //         this.refreshSTComponents();

    //         // Trigger event for attachment handling
    //         this.eventTrigger.next({ id: res.id });

    //         subscriber.next(res);
    //         subscriber.complete();
    //       },
    //       error: (err: any) => {
    //         // Reset loading state on error
    //         this.loading = false;
    //         subscriber.error(err);
    //       }
    //     });
    //   }
    // });

    //   return () => {
    //     // Ensure loading state is reset on unsubscribe
    //     this.loading = false;
    //   };
    // });
  }

  /**
   * Updates an existing SST Return
   */
  update(body: any) {
    const model = { ...body };
    const hasDP = this.deemedPaymentItems?.length > 0;
    const hasDPR = this.deemedPaymentRecoveryItems?.length > 0;
    const hasExcludedDP = this.excludedDeemedPaymentItems?.length > 0;
    const hasExcludedDPR = this.excludedDeemedPaymentRecoveryItems?.length > 0;

    // Include both active and excluded deemed payment items
    if (hasDP || hasExcludedDP) {
      // Important: Ensure isExcluded is explicitly set for all items
      model.sstDeemedPayments = [
        ...(this.deemedPaymentItems || []).map(item => ({
          ...item,
          isExcluded: false, // Explicitly mark as not excluded
          excludeReason: ''
        })),
        ...(this.excludedDeemedPaymentItems || []).map(item => ({
          ...item,
          isExcluded: true, // Explicitly mark as excluded
          excludeReason: item.excludeReason || 'Manually excluded'
        }))
      ];
    }

    // Include both active and excluded deemed payment recovery items
    if (hasDPR || hasExcludedDPR) {
      // Important: Ensure isExcluded is explicitly set for all items
      model.sstDeemedPaymentRecovery = [
        ...(this.deemedPaymentRecoveryItems || []).map(item => ({
          ...item,
          isExcluded: false, // Explicitly mark as not excluded
          excludeReason: ''
        })),
        ...(this.excludedDeemedPaymentRecoveryItems || []).map(item => ({
          ...item,
          isExcluded: true, // Explicitly mark as excluded
          excludeReason: item.excludeReason || 'Manually excluded'
        }))
      ];
    }

    return this.http.post('api/sstreturns/update', model).pipe(
      tap(() => {
        // Trigger event to save attachments
        this.eventTrigger.next({ id: model.id.toString() });
      })
    );
  }

  /**
   * Deletes an SST Return by ID
   */
  delete(id: string) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(`api/sstreturns`, null, { body: { ids: [id] } })
        .subscribe({
          next: res => resolve(res),
          error: () => reject()
        });
    });
  }

  /**
   * Initializes the form with the provided data
   */
  initForm(data: any) {
    // Implementation can be added as needed
  }

  /**
   * Creates and configures the form for SST Return
   */
  createForm() {
    const form = this.fb.group({
      id: [0, []],
      taxableDateFrom: [new Date(), [Validators.required]],
      taxableDateTo: [new Date(), [Validators.required]],
      dueDate: [new Date(), [Validators.required]],
      type: [0, []],
      identityCardNo: ['', []],
      salesTaxDeductionAmount: [<number>0, []],
      serviceTaxDeductionAmount: [<number>0, []],
      adjustmentAmount: [<number>0, []],
      penaltyRate: [<number>0, []],
      penaltyAmount: [<number>0, []],
      code: ['', []],
      status: [false, []],
      totalPayableTax: [<number>0, []],
      details: this.fb.array([this.buildDetailGroup()])
    });

    // // Handle type changes
    // form.controls.type.valueChanges.subscribe(val => {
    //   if (!this.observeChanges) return;
    //   if (val === 1)
    //     this.loadDeemedPayment();
    // });

    // Handle taxable date from changes
    form.controls.taxableDateFrom.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val) {
        let taxableDateTo = new Date(val);
        taxableDateTo.setMonth(taxableDateTo.getMonth() + 1);
        this.form.controls.taxableDateTo.setValue(
          new Date(taxableDateTo.getFullYear(), taxableDateTo.getMonth() + 1, 0)
        );

        if (this.form.controls.taxableDateTo.value) {
          this.form.controls.dueDate.setValue(
            this.getDueDate(this.form.controls.taxableDateTo.value)
          );
        }
        // if (form.controls.type.value === 1) {
        //   this.loadDeemedPayment(true);
        //   this.loadDeemedPaymentRecovery(true);
        // }

        // Reload details and taxable amounts when date changes
        this.reloadDetailsAndTaxableAmounts();
      }
    });

    // Handle taxable date to changes
    form.controls.taxableDateTo.valueChanges.subscribe(val => {
      if (!this.observeChanges) return;
      if (val) {
        let dateTo = new Date(val);
        this.form.controls.dueDate.setValue(this.getDueDate(dateTo));
        // if (form.controls.type.value === 1) {
        //   this.loadDeemedPayment(true);
        //   this.loadDeemedPaymentRecovery(true);
        // }

        // Reload details and taxable amounts when date changes
        this.reloadDetailsAndTaxableAmounts();
      }
    });

    // Handle form value changes
    form.valueChanges.subscribe(() => {
      if (!this.observeChanges) return;
      this.modified = true;
    });
    return form;
  }

  private buildDetailGroup(detail?: any) {
    return this.fb.group({
      taxCode: [detail?.taxCode || '', []],
      totalTaxableAmountLocal: [
        detail?.totalTaxableAmountLocal || 0,
        [Validators.required]
      ],
      totalTaxAmountLocal: [
        detail?.totalTaxAmountLocal || 0,
        [Validators.required]
      ],
      totalNetAmountLocal: [
        detail?.totalNetAmountLocal || 0,
        [Validators.required]
      ],
      detailLists: this.fb.array(
        (detail?.detailLists || []).map((item: any) =>
          this.buildDetailListGroup(item)
        ) || [this.buildDetailListGroup()]
      )
    });
  }

  private buildDetailListGroup(item?: any) {
    return this.fb.group({
      docDate: [item?.docDate ? new Date(item.docDate) : new Date(), []],
      taxDate: [item?.taxDate ? new Date(item.taxDate) : new Date(), []],
      docType: [item?.docType || '', []],
      docCode: [item?.docCode || '', []],
      stockCode: [item?.stockCode || '', []],
      // stockName: [item?.stockName || '', []], // Uncomment if needed
      // accountCode: [item?.accountCode || '', []], // Uncomment if needed
      accountName: [item?.accountName || '', []],
      itemDescription: [item?.itemDescription || '', []],
      taxExclusiveAmountLocal: [
        item?.taxExclusiveAmountLocal || 0,
        [Validators.required]
      ],
      taxAmountLocal: [item?.taxAmountLocal || 0, [Validators.required]],
      netAmountLocal: [item?.netAmountLocal || 0, [Validators.required]]
    });
  }

  /**
   * Opens a document
   */
  openDoc(line: any) {
    this.docNav.openDoc(line.docType, line.docCode);
  }

  /**
   * Opens a journal document
   */
  openJournal(journal: any) {
    if (journal) {
      this.docNav.openDoc('JV', journal.journalCode);
    }
  }

  /**
   * Opens an AR document
   */
  openArDoc(line: any) {
    this.docNav.openDoc('AROR', line.arDocCode);
  }

  /**
   * Opens a payment document
   */
  openPayForDoc(line: any) {
    this.docNav.openDoc(line.payForDocType, line.payForDocCode);
  }

  /**
   * Loads deemed payment data based on the selected date range
   */
  // loadDeemedPayment(reGenerate: boolean = false) {
  //   // Preserve existing excluded items to ensure they stay excluded after reload
  //   const existingExcludedItems = [...this.excludedDeemedPaymentItems];

  //   this.deemedPaymentItems = [];
  //   this.excludedDeemedPaymentItems = [];
  //   const sstReturnId = this._data?.id ?? null;
  //   const isNew = sstReturnId === 0 || sstReturnId === '0';
  //   const from = this.form.controls.taxableDateFrom.value;
  //   const to = this.form.controls.taxableDateTo.value;

  //   if (from != null && to != null) {
  //     const startDate = formatDate(from, 'yyyy-MM-dd', 'en-US');
  //     const endDate = formatDate(to, 'yyyy-MM-dd', 'en-US');

  //     const url = `api/sstreturns/GetDeemedPaymentData?startDate=${startDate}&endDate=${endDate}${
  //       isNew ? '' : `&sstReturnId=${sstReturnId}&reGenerate=${reGenerate}`
  //     }`;
  //     const request = this.http.get(url).pipe(
  //       tap((res: any) => {
  //         // Ensure each item has the isExcluded property defined
  //         const allItems = (res || []).map((item: any) => ({
  //           ...item,
  //           isExcluded: !!item.isExcluded // Ensure boolean value
  //         }));

  //         // Process the server response
  //         this.deemedPaymentItems = allItems.filter(
  //           (item: any) => !item.isExcluded
  //         );
  //         this.excludedDeemedPaymentItems = allItems.filter(
  //           (item: any) => item.isExcluded
  //         );

  //         // Ensure any previously excluded items stay excluded (that might not be marked on server)
  //         if (existingExcludedItems.length > 0) {
  //           for (const excludedItem of existingExcludedItems) {
  //             // Check if we have the item in the active list
  //             const activeIndex = this.deemedPaymentItems.findIndex(
  //               item =>
  //                 item.id === excludedItem.id &&
  //                 item.docCode === excludedItem.docCode &&
  //                 item.docType === excludedItem.docType
  //             );

  //             if (activeIndex !== -1) {
  //               // Move from active to excluded
  //               const item = { ...this.deemedPaymentItems[activeIndex] };
  //               item.isExcluded = true;
  //               item.excludeReason =
  //                 excludedItem.excludeReason || 'Manually excluded';
  //               this.excludedDeemedPaymentItems.push(item);
  //               this.deemedPaymentItems.splice(activeIndex, 1);
  //             }
  //           }
  //         }

  //         // Store original copies for cancel operation
  //         this._originalDeemedPaymentItems = JSON.parse(
  //           JSON.stringify(this.deemedPaymentItems)
  //         );
  //         this._originalExcludedDeemedPaymentItems = JSON.parse(
  //           JSON.stringify(this.excludedDeemedPaymentItems)
  //         );

  //         // Force refresh of tables
  //         this.refreshAllTables();
  //       })
  //     );

  //     // Auto-subscribe when called from tab click or date change
  //     request.subscribe();

  //     return request; // Still return for create() to use
  //   }
  //   return of([]);
  // }

  // /**
  //  * Loads deemed payment recovery data based on the selected date range
  //  */
  // loadDeemedPaymentRecovery(reGenerate: boolean = false) {
  //   // Preserve existing excluded items to ensure they stay excluded after reload
  //   const existingExcludedItems = [...this.excludedDeemedPaymentRecoveryItems];

  //   this.deemedPaymentRecoveryItems = [];
  //   this.excludedDeemedPaymentRecoveryItems = [];
  //   const sstReturnId = this._data?.id ?? null;
  //   const isNew = sstReturnId === 0 || sstReturnId === '0';
  //   const from = this.form.controls.taxableDateFrom.value;
  //   const to = this.form.controls.taxableDateTo.value;

  //   if (from != null && to != null) {
  //     const startDate = formatDate(from, 'yyyy-MM-dd', 'en-US');
  //     const endDate = formatDate(to, 'yyyy-MM-dd', 'en-US');

  //     const url = `api/sstreturns/GetDeemedPaymentRecoveryData?startDate=${startDate}&endDate=${endDate}${
  //       isNew ? '' : `&sstReturnId=${sstReturnId}&reGenerate=${reGenerate}`
  //     }`;
  //     const request = this.http.get(url).pipe(
  //       tap((res: any) => {
  //         // Ensure each item has the isExcluded property defined
  //         const allItems = (res || []).map((item: any) => ({
  //           ...item,
  //           isExcluded: !!item.isExcluded // Ensure boolean value
  //         }));

  //         // Process the server response
  //         this.deemedPaymentRecoveryItems = allItems.filter(
  //           (item: any) => !item.isExcluded
  //         );
  //         this.excludedDeemedPaymentRecoveryItems = allItems.filter(
  //           (item: any) => item.isExcluded
  //         );

  //         // Ensure any previously excluded items stay excluded (that might not be marked on server)
  //         if (existingExcludedItems.length > 0) {
  //           for (const excludedItem of existingExcludedItems) {
  //             // Check if we have the item in the active list
  //             const activeIndex = this.deemedPaymentRecoveryItems.findIndex(
  //               item =>
  //                 item.id === excludedItem.id &&
  //                 item.arDocCode === excludedItem.arDocCode &&
  //                 item.payForDocCode === excludedItem.payForDocCode
  //             );

  //             if (activeIndex !== -1) {
  //               // Move from active to excluded
  //               const item = {
  //                 ...this.deemedPaymentRecoveryItems[activeIndex]
  //               };
  //               item.isExcluded = true;
  //               item.excludeReason =
  //                 excludedItem.excludeReason || 'Manually excluded';
  //               this.excludedDeemedPaymentRecoveryItems.push(item);
  //               this.deemedPaymentRecoveryItems.splice(activeIndex, 1);
  //             }
  //           }
  //         }

  //         // Store original copies for cancel operation
  //         this._originalDeemedPaymentRecoveryItems = JSON.parse(
  //           JSON.stringify(this.deemedPaymentRecoveryItems)
  //         );
  //         this._originalExcludedDeemedPaymentRecoveryItems = JSON.parse(
  //           JSON.stringify(this.excludedDeemedPaymentRecoveryItems)
  //         );

  //         // Force refresh of tables
  //         this.refreshAllTables();
  //       })
  //     );

  //     // Auto-subscribe when called from tab click or date change
  //     request.subscribe();

  //     return request; // Still return for create() to use
  //   }
  //   return of([]);
  // }

  /**
   * Calculates the due date based on the given date
   */
  getDueDate(date: Date): Date {
    let dueDate = new Date(date.getFullYear(), date.getMonth(), 1);
    dueDate.setMonth(dueDate.getMonth() + 1);
    dueDate = new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, 0);

    return dueDate;
  }

  // Store selections for each group separately
  private groupSelections: Map<string, any[]> = new Map();
  private excludedGroupSelections: Map<string, any[]> = new Map();

  /**
   * Handles checkbox selection changes for included deemed payment items
   */
  onDeemedPaymentCheckboxChange(e: any, group?: any): void {
    // Only update selection when event type is checkbox
    if (e.type === 'checkbox') {
      // Store the selection for this specific group
      if (group && group.taxCode) {
        this.groupSelections.set(group.taxCode, e.checkbox || []);
      }
      this.updateCombinedDeemedPaymentSelection();
    }
  }

  /**
   * Updates the combined selection from all deemed payment groups
   */
  private updateCombinedDeemedPaymentSelection(): void {
    // Get all selected items from all groups
    const allSelectedItems: any[] = [];

    // Combine selections from all groups
    this.groupSelections.forEach((selectedItems, taxCode) => {
      allSelectedItems.push(...selectedItems);
    });

    this.selectedDeemedPaymentRows = allSelectedItems;
  }

  /**
   * Handles checkbox selection changes for excluded deemed payment items
   */
  onExcludedDeemedPaymentCheckboxChange(e: any, group?: any): void {
    // Only update selection when event type is checkbox
    if (e.type === 'checkbox') {
      // Store the selection for this specific group
      if (group && group.taxCode) {
        this.excludedGroupSelections.set(group.taxCode, e.checkbox || []);
      }
      this.updateCombinedExcludedDeemedPaymentSelection();
    }
  }

  /**
   * Updates the combined selection from all excluded deemed payment groups
   */
  private updateCombinedExcludedDeemedPaymentSelection(): void {
    // Get all selected items from all excluded groups
    const allSelectedItems: any[] = [];

    // Combine selections from all excluded groups
    this.excludedGroupSelections.forEach((selectedItems, taxCode) => {
      allSelectedItems.push(...selectedItems);
    });

    this.selectedExcludedDeemedPaymentRows = allSelectedItems;
  }

  /**
   * Handles checkbox selection changes for included deemed payment recovery items
   */
  onDeemedPaymentRecoveryCheckboxChange(e: any): void {
    // Only update selection when event type is checkbox
    if (e.type === 'checkbox') {
      this.selectedDeemedPaymentRecoveryRows = e.checkbox || [];
    }
  }

  /**
   * Handles checkbox selection changes for excluded deemed payment recovery items
   */
  onExcludedDeemedPaymentRecoveryCheckboxChange(e: any): void {
    // Only update selection when event type is checkbox
    if (e.type === 'checkbox') {
      this.selectedExcludedDeemedPaymentRecoveryRows = e.checkbox || [];
    }
  }

  /**
   * Excludes selected deemed payment items
   */
  excludeSelectedDeemedPayments(): void {
    if (this.selectedDeemedPaymentRows.length === 0) {
      return;
    }

    const modalService = this.injector.get(NzModalService);

    const modal = modalService.create({
      nzTitle: 'Exclude Records',
      nzContent: ExclusionModalComponent,
      nzFooter: null,
      nzWidth: 500,
      nzMaskClosable: false,
      nzBodyStyle: { padding: '24px' }
    });

    const instance = modal.getContentComponent();
    instance.confirmed.subscribe((reason: string) => {
      this.confirmExcludeDeemedPayments(reason);
    });
  }

  /**
   * Confirms exclusion of deemed payment items
   */
  private confirmExcludeDeemedPayments(reason: string): void {
    const message = this.injector.get(NzMessageService);

    // Store the count before processing
    const count = this.selectedDeemedPaymentRows.length;

    // Process each selected item
    for (const selectedItem of this.selectedDeemedPaymentRows) {
      // Find the item in the active list - using more properties to ensure correct match
      const index = this.deemedPaymentItems.findIndex(
        item =>
          item.id === selectedItem.id &&
          item.docCode === selectedItem.docCode &&
          item.docType === selectedItem.docType
      );

      if (index !== -1) {
        // Get a deep copy of the item to avoid reference issues
        const item = JSON.parse(JSON.stringify(this.deemedPaymentItems[index]));

        // Mark as excluded with reason - IMPORTANT: Use strict boolean true
        item.isExcluded = true;
        item.excludeReason = reason;
        selectedItem.isExcluded = true;
        selectedItem.excludeReason = reason;

        // Update total payable tax
        const currentTax = this.form.controls.totalPayableTax.value ?? 0;
        this.form.controls.totalPayableTax.setValue(
          currentTax - item.osTaxAmountLocal
        );

        // Add to excluded list (moves from active to excluded tab)
        this.excludedDeemedPaymentItems.push(item);

        // Remove from active list
        this.deemedPaymentItems.splice(index, 1);
      }
    }

    // Clear selection
    this.selectedDeemedPaymentRows = [];
    this.groupSelections.clear();

    // Regroup the data for UI display
    this.deemedPaymentGroups = this.groupDeemedPaymentItems(
      this.deemedPaymentItems
    );
    this.excludedDeemedPaymentGroups = this.groupDeemedPaymentItems(
      this.excludedDeemedPaymentItems
    );

    // Force UI update immediately to show changes in both active and excluded lists
    this.refreshAllTables();

    // Show success message
    message.success(`${count} deemed payment item(s) excluded.`);
  }

  /**
   * Excludes selected deemed payment recovery items
   */
  excludeSelectedDeemedPaymentRecoveries(): void {
    if (this.selectedDeemedPaymentRecoveryRows.length === 0) {
      return;
    }

    const modalService = this.injector.get(NzModalService);

    const modal = modalService.create({
      nzTitle: 'Exclude Records',
      nzContent: ExclusionModalComponent,
      nzFooter: null,
      nzWidth: 500,
      nzMaskClosable: false,
      nzBodyStyle: { padding: '24px' }
    });

    const instance = modal.getContentComponent();
    instance.confirmed.subscribe((reason: string) => {
      this.confirmExcludeDeemedPaymentRecoveries(reason);
    });
  }

  /**
   * Confirms exclusion of deemed payment recovery items
   */
  private confirmExcludeDeemedPaymentRecoveries(reason: string): void {
    const message = this.injector.get(NzMessageService);

    // Store the count before processing
    const count = this.selectedDeemedPaymentRecoveryRows.length;

    // Process each selected item
    for (const selectedItem of this.selectedDeemedPaymentRecoveryRows) {
      // Find the item in the active list - using more properties to ensure correct match
      const index = this.deemedPaymentRecoveryItems.findIndex(
        item =>
          item.id === selectedItem.id &&
          item.arDocCode === selectedItem.arDocCode &&
          item.payForDocCode === selectedItem.payForDocCode
      );

      if (index !== -1) {
        // Get a deep copy of the item to avoid reference issues
        const item = JSON.parse(
          JSON.stringify(this.deemedPaymentRecoveryItems[index])
        );

        // Mark as excluded with reason - IMPORTANT: Use strict boolean true
        item.isExcluded = true;
        item.excludeReason = reason;
        selectedItem.isExcluded = true;
        selectedItem.excludeReason = reason;

        // Update total payable tax
        const currentTax = this.form.controls.totalPayableTax.value ?? 0;
        this.form.controls.totalPayableTax.setValue(
          currentTax + item.osTaxAmountLocal
        );

        // Add to excluded list (moves from active to excluded tab)
        this.excludedDeemedPaymentRecoveryItems.push(item);

        // Remove from active list
        this.deemedPaymentRecoveryItems.splice(index, 1);
      }
    }

    // Clear selection
    this.selectedDeemedPaymentRecoveryRows = [];

    // Regroup the data for UI display
    this.deemedPaymentRecoveryGroups = this.groupDeemedPaymentRecoveryItems(
      this.deemedPaymentRecoveryItems
    );
    this.excludedDeemedPaymentRecoveryGroups =
      this.groupDeemedPaymentRecoveryItems(
        this.excludedDeemedPaymentRecoveryItems
      );

    // Force UI update immediately to show changes in both active and excluded lists
    this.refreshAllTables();

    // Show success message
    message.success(`${count} deemed payment recovery item(s) excluded.`);
  }

  /**
   * Includes selected deemed payment items (moves them from excluded to active)
   */
  includeSelectedDeemedPayments(): void {
    if (this.selectedExcludedDeemedPaymentRows.length === 0) {
      return;
    }

    const message = this.injector.get(NzMessageService);
    const modalService = this.injector.get(NzModalService);

    modalService.confirm({
      nzTitle: 'Include Records',
      nzContent:
        'Are you sure you want to include these records back to the active list?',
      nzOkText: 'Include',
      nzOkType: 'primary',
      nzOnOk: () => {
        // Store the count before processing
        const count = this.selectedExcludedDeemedPaymentRows.length;

        // Process each selected item
        for (const selectedItem of this.selectedExcludedDeemedPaymentRows) {
          // Find the item in the excluded list
          const index = this.excludedDeemedPaymentItems.findIndex(
            item => item.id === selectedItem.id
          );
          if (index !== -1) {
            // Get a copy of the item
            const item = { ...this.excludedDeemedPaymentItems[index] };

            // Mark as included by resetting exclusion flags
            item.isExcluded = false;
            item.excludeReason = '';

            // Update total payable tax
            const currentTax = this.form.controls.totalPayableTax.value ?? 0;
            this.form.controls.totalPayableTax.setValue(
              currentTax + item.osTaxAmountLocal
            );

            // Add to active list (moves from excluded to active tab)
            this.deemedPaymentItems.push(item);

            // Remove from excluded list
            this.excludedDeemedPaymentItems.splice(index, 1);
          }
        }

        // Clear selection
        this.selectedExcludedDeemedPaymentRows = [];
        this.excludedGroupSelections.clear();

        // Regroup the data for UI display
        this.deemedPaymentGroups = this.groupDeemedPaymentItems(
          this.deemedPaymentItems
        );
        this.excludedDeemedPaymentGroups = this.groupDeemedPaymentItems(
          this.excludedDeemedPaymentItems
        );

        // Force UI update immediately to show changes in both lists
        this.refreshAllTables();

        // Show success message
        message.success(`${count} deemed payment item(s) included.`);
      }
    });
  }

  /**
   * Includes selected deemed payment recovery items (moves them from excluded to active)
   */
  includeSelectedDeemedPaymentRecoveries(): void {
    if (this.selectedExcludedDeemedPaymentRecoveryRows.length === 0) {
      return;
    }

    const message = this.injector.get(NzMessageService);
    const modalService = this.injector.get(NzModalService);

    modalService.confirm({
      nzTitle: 'Include Records',
      nzContent:
        'Are you sure you want to include these records back to the active list?',
      nzOkText: 'Include',
      nzOkType: 'primary',
      nzOnOk: () => {
        // Store the count before processing
        const count = this.selectedExcludedDeemedPaymentRecoveryRows.length;

        // Process each selected item
        for (const selectedItem of this
          .selectedExcludedDeemedPaymentRecoveryRows) {
          // Find the item in the excluded list
          const index = this.excludedDeemedPaymentRecoveryItems.findIndex(
            item => item.id === selectedItem.id
          );
          if (index !== -1) {
            // Get a copy of the item
            const item = { ...this.excludedDeemedPaymentRecoveryItems[index] };

            // Mark as included by resetting exclusion flags
            item.isExcluded = false;
            item.excludeReason = '';

            // Update total payable tax
            const currentTax = this.form.controls.totalPayableTax.value ?? 0;
            this.form.controls.totalPayableTax.setValue(
              currentTax - item.osTaxAmountLocal
            );

            // Add to active list (moves from excluded to active tab)
            this.deemedPaymentRecoveryItems.push(item);

            // Remove from excluded list
            this.excludedDeemedPaymentRecoveryItems.splice(index, 1);
          }
        }

        // Clear selection
        this.selectedExcludedDeemedPaymentRecoveryRows = [];

        // Regroup the data for UI display
        this.deemedPaymentRecoveryGroups = this.groupDeemedPaymentRecoveryItems(
          this.deemedPaymentRecoveryItems
        );
        this.excludedDeemedPaymentRecoveryGroups =
          this.groupDeemedPaymentRecoveryItems(
            this.excludedDeemedPaymentRecoveryItems
          );

        // Force UI update immediately to show changes in both lists
        this.refreshAllTables();

        // Show success message
        message.success(`${count} deemed payment recovery item(s) included.`);
      }
    });
  }

  //#endregion

  //#region Private Helpers

  /**
   * Checks if an error is related to missing SST settings
   */
  private isSstSettingsMissingError(err: any): boolean {
    return (
      (err.error &&
        err.error.message === 'SST settings are missing or incomplete.') ||
      (err.error && err.error.code === 'ERR_MISSING_SST_SETTINGS')
    );
  }

  /**
   * Reloads details and taxable amounts based on current date range
   */
  private reloadDetailsAndTaxableAmounts(): void {
    const dateFrom = this.form.controls.taxableDateFrom.value;
    const dateTo = this.form.controls.taxableDateTo.value;

    if (dateFrom && dateTo) {
      // Convert null values to undefined before passing to new()
      const fromDate = dateFrom
        ? formatDate(dateFrom, 'yyyy-MM-dd', 'en-US')
        : undefined;
      const toDate = dateTo
        ? formatDate(dateTo, 'yyyy-MM-dd', 'en-US')
        : undefined;

      this.new(fromDate, toDate).subscribe();
    }
  }
  //#endregion

  //#region Table Configurations

  @ViewChild('stDeemedPayment') stDeemedPayment!: STComponent;
  @ViewChild('stDeemedPaymentRecovery') stDeemedPaymentRecovery!: STComponent;
  @ViewChild('stExcludedDeemedPayment') stExcludedDeemedPayment!: STComponent;
  @ViewChild('stExcludedDeemedPaymentRecovery')
  stExcludedDeemedPaymentRecovery!: STComponent;

  /**
   * Column configuration for deemed payment table
   */
  deemedPaymentColumns: STColumn[] = [
    {
      title: '',
      index: 'id',
      type: 'checkbox',
      width: 50
    },
    {
      index: 'isExcluded',
      title: '',
      width: 50,
      render: 'excludedTpl'
    },
    {
      index: 'docDate',
      title: 'Doc Date',
      width: 120
    },
    {
      index: 'docType',
      title: 'Doc Type',
      width: 80
    },
    {
      index: 'docCode',
      title: 'Doc Code',
      width: 120,
      render: 'doccodeTpl'
    },
    {
      index: 'customer.code',
      title: 'Customer Code',
      width: 120
    },
    {
      index: 'customer.name',
      title: 'Customer Name',
      width: 220
    },
    {
      index: 'osTaxableAmount',
      title: 'O/S Taxable Amount',
      width: 120,
      render: 'osTaxableTpl',
      className: 'text-right'
    },
    {
      index: 'taxCode.code',
      title: 'Tax Code',
      width: 120
    },
    {
      index: 'osTaxAmountLocal',
      title: 'O/S Local Tax',
      width: 120,
      render: 'osTaxAmtTpl',
      className: 'text-right'
    },
    {
      index: 'osNetAmountLocal',
      title: 'Net Amount',
      width: 120,
      render: 'osNetAmtTpl',
      className: 'text-right'
    },
    {
      index: 'journalCode',
      title: 'Journal',
      width: 120,
      render: 'journalTpl'
    }
  ];

  /**
   * Column configuration for excluded deemed payment table
   */
  excludedDeemedPaymentColumns: STColumn[] = [
    {
      title: '',
      index: 'id',
      type: 'checkbox',
      width: 50
    },
    {
      index: 'docDate',
      title: 'Doc Date',
      width: 120
    },
    {
      index: 'docType',
      title: 'Doc Type',
      width: 80
    },
    {
      index: 'docCode',
      title: 'Doc Code',
      width: 120,
      render: 'doccodeTpl'
    },
    {
      index: 'customer.code',
      title: 'Customer Code',
      width: 120
    },
    {
      index: 'customer.name',
      title: 'Customer Name',
      width: 220
    },
    {
      index: 'osTaxableAmount',
      title: 'O/S Taxable Amount',
      width: 120,
      render: 'osTaxableTpl',
      className: 'text-right'
    },
    {
      index: 'taxCode.code',
      title: 'Tax Code',
      width: 120
    },
    {
      index: 'osTaxAmountLocal',
      title: 'O/S Local Tax',
      width: 120,
      render: 'osTaxAmtTpl',
      className: 'text-right'
    },
    {
      index: 'osNetAmountLocal',
      title: 'Net Amount',
      width: 120,
      render: 'osNetAmtTpl',
      className: 'text-right'
    },
    {
      index: 'excludeReason',
      title: 'Exclude Reason',
      width: 250
    }
  ];

  /**
   * Column configuration for deemed payment recovery table
   */
  deemedPaymentRecoveryColumns: STColumn[] = [
    {
      title: '',
      index: 'id',
      type: 'checkbox',
      width: 50
    },
    {
      title: {
        text: 'Received Details ',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'arDocType',
          title: 'Doc Type',
          width: 80
        },
        {
          index: 'arDocCode',
          title: 'Payment Doc.',
          width: 120,
          render: 'ardoccodeTpl'
        },
        {
          index: 'arDocDate',
          title: 'Doc Date',
          width: 120
        },
        {
          index: 'taxDate',
          title: 'Tax Date',
          width: 120
        },
        {
          index: 'knockoffAmountLocal',
          title: 'Knockoff Amt',
          width: 120,
          render: 'knockoffAmtTpl',
          className: 'text-right'
        }
      ]
    },
    {
      title: {
        text: 'Outstanding Documents',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'payForDocType',
          title: 'Doc Type',
          width: 80
        },
        {
          index: 'payForDocCode',
          title: 'Payment Doc.',
          width: 120,
          render: 'doccodeTpl'
        },
        {
          index: 'taxCode.code',
          title: 'Tax Code',
          width: 120
        },
        {
          index: 'osTaxableAmount',
          title: 'Taxable Amount',
          width: 120,
          render: 'osTaxableTpl',
          className: 'text-right'
        },
        {
          index: 'osTaxAmountLocal',
          title: 'Tax',
          width: 110,
          render: 'osTaxAmtTpl',
          className: 'text-right'
        },
        {
          index: 'osNetAmountLocal',
          title: 'Net Amount',
          width: 120,
          render: 'osNetAmtTpl',
          className: 'text-right'
        }
      ]
    },
    {
      title: {
        text: 'SST Recovery',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'TaxableAmountLocal',
          title: 'Taxable Amount',
          width: 120,
          render: 'taxableAmtTpl',
          className: 'text-right'
        },
        {
          index: 'taxAmountLocal',
          title: 'Service Tax Recovery',
          width: 180,
          render: 'taxAmtTpl',
          className: 'text-right'
        },
        {
          index: 'journalCode',
          title: 'Journal',
          width: 120,
          render: 'journalTpl'
        }
      ]
    }
  ];

  /**
   * Column configuration for excluded deemed payment recovery table
   */
  excludedDeemedPaymentRecoveryColumns: STColumn[] = [
    {
      title: '',
      index: 'id',
      type: 'checkbox',
      width: 50
    },
    {
      title: {
        text: 'Received Details',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'arDocType',
          title: 'Doc Type',
          width: 80
        },
        {
          index: 'arDocCode',
          title: 'Payment Doc.',
          width: 120,
          render: 'ardoccodeTpl'
        },
        {
          index: 'arDocDate',
          title: 'Doc Date',
          width: 120
        },
        {
          index: 'taxDate',
          title: 'Tax Date',
          width: 120
        },
        {
          index: 'knockoffAmountLocal',
          title: 'Knockoff Amt',
          width: 120,
          render: 'knockoffAmtTpl',
          className: 'text-right'
        }
      ]
    },
    {
      title: {
        text: 'Outstanding Documents',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'payForDocType',
          title: 'Doc Type',
          width: 80
        },
        {
          index: 'payForDocCode',
          title: 'Payment Doc.',
          width: 120,
          render: 'doccodeTpl'
        },
        {
          index: 'taxCode.code',
          title: 'Tax Code',
          width: 120
        },
        {
          index: 'osTaxableAmount',
          title: 'Taxable Amount',
          width: 120,
          render: 'osTaxableTpl',
          className: 'text-right'
        },
        {
          index: 'osTaxAmountLocal',
          title: 'Tax',
          width: 110,
          render: 'osTaxAmtTpl',
          className: 'text-right'
        },
        {
          index: 'osNetAmountLocal',
          title: 'Net Amount',
          width: 120,
          render: 'osNetAmtTpl',
          className: 'text-right'
        }
      ]
    },
    {
      title: {
        text: 'SST Recovery',
        style: {
          'text-align': 'center'
        }
      },
      children: [
        {
          index: 'taxAmountLocal',
          title: 'Service Tax Recovery',
          width: 180,
          render: 'taxAmtTpl',
          className: 'text-right'
        },
        {
          index: 'excludeReason',
          title: 'Exclude Reason',
          width: 250
        }
      ]
    }
  ];

  /**
   * Refreshes all ST components if needed
   */
  private refreshSTComponents(): void {
    setTimeout(() => {
      // Force table refreshes if available
      if (this.stDeemedPayment) {
        if (this.stDeemedPayment.reload) {
          this.stDeemedPayment.reload();
        } else if (this.stDeemedPayment.reset) {
          this.stDeemedPayment.reset();
        }
      }

      if (this.stExcludedDeemedPayment) {
        if (this.stExcludedDeemedPayment.reload) {
          this.stExcludedDeemedPayment.reload();
        } else if (this.stExcludedDeemedPayment.reset) {
          this.stExcludedDeemedPayment.reset();
        }
      }

      if (this.stDeemedPaymentRecovery) {
        if (this.stDeemedPaymentRecovery.reload) {
          this.stDeemedPaymentRecovery.reload();
        } else if (this.stDeemedPaymentRecovery.reset) {
          this.stDeemedPaymentRecovery.reset();
        }
      }

      if (this.stExcludedDeemedPaymentRecovery) {
        if (this.stExcludedDeemedPaymentRecovery.reload) {
          this.stExcludedDeemedPaymentRecovery.reload();
        } else if (this.stExcludedDeemedPaymentRecovery.reset) {
          this.stExcludedDeemedPaymentRecovery.reset();
        }
      }
    }, 100);
  }

  /**
   * Refreshes all ST tables and forces UI update
   */
  private refreshAllTables(): void {
    // First use the existing refresh method
    this.refreshSTComponents();

    // Force Angular change detection
    setTimeout(() => {
      // Make sure data arrays are properly processed
      this.deemedPaymentItems = this.deemedPaymentItems.filter(
        item => !item.isExcluded
      );
      this.excludedDeemedPaymentItems = this.excludedDeemedPaymentItems.filter(
        item => item.isExcluded
      );
      this.deemedPaymentRecoveryItems = this.deemedPaymentRecoveryItems.filter(
        item => !item.isExcluded
      );
      this.excludedDeemedPaymentRecoveryItems =
        this.excludedDeemedPaymentRecoveryItems.filter(item => item.isExcluded);

      // Check if tables exist and reload data
      if (this.stDeemedPayment) {
        this.stDeemedPayment.data = [...this.deemedPaymentItems];
        if (this.stDeemedPayment.reload) this.stDeemedPayment.reload();
      }

      if (this.stExcludedDeemedPayment) {
        // Make sure we're using the correct columns with Exclude Reason
        this.stExcludedDeemedPayment.data = [
          ...this.excludedDeemedPaymentItems
        ];
        if (this.stExcludedDeemedPayment.reload)
          this.stExcludedDeemedPayment.reload();
      }

      if (this.stDeemedPaymentRecovery) {
        this.stDeemedPaymentRecovery.data = [
          ...this.deemedPaymentRecoveryItems
        ];
        if (this.stDeemedPaymentRecovery.reload)
          this.stDeemedPaymentRecovery.reload();
      }

      if (this.stExcludedDeemedPaymentRecovery) {
        // Make sure we're using the correct columns with Exclude Reason
        this.stExcludedDeemedPaymentRecovery.data = [
          ...this.excludedDeemedPaymentRecoveryItems
        ];
        if (this.stExcludedDeemedPaymentRecovery.reload)
          this.stExcludedDeemedPaymentRecovery.reload();
      }

      // Force another refresh after a brief delay to ensure changes are visible
      setTimeout(() => {
        if (this.stDeemedPayment && this.stDeemedPayment.reload)
          this.stDeemedPayment.reload();
        if (this.stExcludedDeemedPayment && this.stExcludedDeemedPayment.reload)
          this.stExcludedDeemedPayment.reload();
        if (this.stDeemedPaymentRecovery && this.stDeemedPaymentRecovery.reload)
          this.stDeemedPaymentRecovery.reload();
        if (
          this.stExcludedDeemedPaymentRecovery &&
          this.stExcludedDeemedPaymentRecovery.reload
        )
          this.stExcludedDeemedPaymentRecovery.reload();
      }, 50);
    }, 0);
  }

  /**
   * Override the base resetDataToOriginal to handle deemed payment arrays
   * This is called when a user cancels their changes
   */
  override resetDataToOriginal() {
    // First call the parent method to reset the form data
    super.resetDataToOriginal();

    // Restore deemed payment arrays from cached original data
    this.deemedPaymentItems = JSON.parse(
      JSON.stringify(this._originalDeemedPaymentItems)
    );
    this.excludedDeemedPaymentItems = JSON.parse(
      JSON.stringify(this._originalExcludedDeemedPaymentItems)
    );
    this.deemedPaymentRecoveryItems = JSON.parse(
      JSON.stringify(this._originalDeemedPaymentRecoveryItems)
    );
    this.excludedDeemedPaymentRecoveryItems = JSON.parse(
      JSON.stringify(this._originalExcludedDeemedPaymentRecoveryItems)
    );

    // Reset selection arrays
    this.selectedDeemedPaymentRows = [];
    this.selectedDeemedPaymentRecoveryRows = [];
    this.selectedExcludedDeemedPaymentRows = [];
    this.selectedExcludedDeemedPaymentRecoveryRows = [];
    this.groupSelections.clear();
    this.excludedGroupSelections.clear();

    // Force refresh all tables
    this.refreshAllTables();
  }

  //#endregion
}
